import type { ReactNode } from 'react';
import type { MContentHeaderProps } from '../../molecules/MContentHeader/MContentHeader.types';
import type { MSlotProps } from '../../molecules/MSlot/MSlot.types';

export type OMiniCardContainerAemReactComponentMapping = NonNullable<MSlotProps['aemReactComponentMapping']>;

/**
 * AEM node after `transformToCQ` (`:items` → `cqItems`, `:type` → `cqType`).
 * Child cards keep `:type` for `MSlot` resource lookup.
 * MapTo may still pass raw `:items` / `:itemsOrder` at the top level.
 */
export interface OMiniCardContainerCqItem {
  ':type'?: string;
  ':items'?: Record<string, OMiniCardContainerCqItem>;
  ':itemsOrder'?: string[];
  cqType?: string;
  cqItems?: Record<string, OMiniCardContainerCqItem>;
  cqItemsOrder?: string[];
  [key: string]: unknown;
}

export type OMiniCardContainerModel = OMiniCardContainerCqItem;

export interface OMiniCardContainerProps
  extends Pick<OMiniCardContainerCqItem, ':items' | ':itemsOrder' | 'cqItems' | 'cqItemsOrder'> {
  /** Desktop column count. `2` or `3`. Tablet and mobile are always 1 column. Default `2`. */
  desktopColumns?: string;
  /**
   * Fixed AEM slot count. When provided, renders `item0` through `item{N-1}`
   * (capped at 9). Each slot is its own `MSlot` parsys so empty slots stay
   * droppable without waiting for a parent `model.json` refresh.
   */
  totalNoOfCards?: number | string;
  mContentHeader?: MContentHeaderProps;
  cqPath?: string;
  pagePath?: string;
  itemPath?: string;
  isAuthorMode?: boolean;
  aemReactComponentMapping?: OMiniCardContainerAemReactComponentMapping;
  className?: string;
  trackingProperties?: OMiniCardContainerTrackingProperties;
  /**
   * Storybook / non-AEM escape hatch. When provided, each child becomes a card
   * and AEM item data is ignored. Capped at 9 cards.
   */
  children?: ReactNode;
  /**
   * AEM chrome and leftover model fields (`styleclass`, `componentId`).
   * Authored cards come from top-level `:items` / `cqItems`, not nested under `model`.
   * `model[':items']` / `model.cqItems` are defensive fallbacks.
   */
  model?: OMiniCardContainerModel;
}

export interface OMiniCardContainerTrackingProperties {
  additionalTrackingVariables?: string;
  enableScrollingBehavior?: boolean;
  trackingDescription?: string;
  impressionTrack?: boolean;
  description?: string;
  trackingContentPosition?: string;
  faicUrl?: string;
  faicMCD?: string;
  atCCeVar48?: string;
  rightArrowDesc?: string;
  cardLocation?: string;
  leftArrowDesc?: string;
  clickTrack?: boolean;
  location?: string;
  merchandisingCategory?: string;
  impressionCount?: boolean;
  trackingTag?: string;
  trackingOfferType?: string;
}

export enum OMiniCardContainerDesktopColumns {
  Two = '2',
  Three = '3',
}

export enum OMiniCardContainerTotalCards {
  One = '1',
  Two = '2',
  Three = '3',
  Four = '4',
  Five = '5',
  Six = '6',
  Seven = '7',
  Eight = '8',
  Nine = '9',
}
